import React, { useRef } from "react";
import * as XLSX from 'xlsx';
import { Peserta } from "../../../interfaces/Monev";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/rootReducer";
import { showModal } from "../../../reducers/modal/modal.actions";
import { updateDeskripsiPeserta, updateFileInputPeserta, updatePeserta } from "../../../reducers/monev/monev.actions";
import { fieldErrorMessage, fieldHasErrors } from "../../../store/errorHandler";
import { PermissionMenuName } from "../../../interfaces/UserRole";
import { allowSave } from "../../../store/helpers";

export const FormPeserta = () => {
  const dispatch = useDispatch();

  const fileInput = useRef<HTMLInputElement>(null);
  const errors = useSelector((state: RootState) => state.monev.error);
  const isLoading = useSelector((state: RootState) => state.monev.isLoading);
  const peserta = useSelector((state: RootState) => state.monev.selectedMonev?.PESERTA_DETAIL);
  const deskripsiPeserta = useSelector((state: RootState) => state.monev.selectedMonev?.PESERTA_KEGIATAN);
  const role = useSelector((state: RootState) => state.authorization.systemAccess[PermissionMenuName.monevPartner].ACCESS);

  const canSave = allowSave(role, 3);

  const addPeserta = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files;
    dispatch(updateFileInputPeserta(e.target.value));
    
    const newPeserta: Peserta[] = [];
    if (selectedFile && selectedFile[0]) {
      const fileReader = new FileReader();
      fileReader.onload = (event) => {
        const data = event.target?.result;
        if (data) {
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheet = XLSX.utils.sheet_to_json(workbook.Sheets['Sheet1']);
          sheet.forEach((item: any) => {
            newPeserta.push({
              NAMA_PESERTA: item['nama'].toString(),
              ID_PESERTA: item['id'].toString(),
              KETERANGAN_PESERTA: item['keterangan'].toString()
            });
          });

          if (newPeserta.length === 0) {
            dispatch(showModal(
              'Gagal Mengunggah Berkas Excel',
              'Sistem tidak menemukan data peserta pada berkas Excel yang Anda unggah. Anda dapat mengunduh template Excel terlebih dahulu dan mengubah sesuai kebutuhan Anda.'
            ));
            if (fileInput && fileInput.current) {
              fileInput.current.value = '';
            }
            return;
          }
          dispatch(updatePeserta(newPeserta));
        }
      }
      fileReader.readAsBinaryString(selectedFile[0]);
    }
  }

  const onChangeDeskripsiPeserta = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(updateDeskripsiPeserta(e.target.value));
  }

  const removePeserta = (id: number) => {
    if (typeof peserta === 'undefined') return;
    const newPeserta = [...peserta];
    newPeserta.splice(id, 1);
    dispatch(updatePeserta(newPeserta));
  }

  const downloadTemplate = () => {
    window.open(`/templates/kerma-impor-peserta.xlsx`, "");
  }

  const renderPeserta = () => {
    if (peserta?.length === 0) {
      return (
        <tr>
          <td colSpan={4} style={{textAlign: 'center'}}>
            Data tidak ditemukan, silahkan tambah peserta baru.
          </td>
        </tr>
      );
    }

    return peserta?.map((item, index) => {
      return (
        <tr key={index}>
          <td>{item.NAMA_PESERTA}</td>
          <td>{item.ID_PESERTA}</td>
          <td>{item.KETERANGAN_PESERTA}</td>
          <td>
            <button className="btn btn-danger btn-xs" disabled={isLoading || !canSave} onClick={() => removePeserta(index)}>
              <i className="fa fa-times" aria-hidden="true"></i>
            </button>
          </td>
        </tr>
      );
    });
  }

  if (isLoading) return (
    <React.Fragment>
      <div className="box">
        <div className="box-header with-border">
          <h3 className="box-title">Peserta Kegiatan</h3>
        </div>
        <div className="box-body">
          Sedang memuat data...
        </div>
        <div className="overlay active">
          <i className="fa fa-refresh fa-spin"></i>
        </div>
      </div>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <div className="box">
        <div className="box-header with-border">
          <h3 className="box-title">Peserta Kegiatan<span className="text-red"><b>*</b></span></h3>
        </div>
        <div className="box-body">
          <div className={`form-group ${fieldHasErrors('PESERTA_KEGIATAN', errors) ? 'has-error' : ''}`}>
            <label>Masukkan Peserta Kegiatan</label>
            <textarea className="form-control" disabled={isLoading || !canSave} value={deskripsiPeserta} onChange={onChangeDeskripsiPeserta} />
            <span className="help-block">{ fieldErrorMessage('PESERTA_KEGIATAN', errors) }</span>
          </div>
          <div className={`form-group ${fieldHasErrors('PESERTA_DETAIL', errors) ? 'has-error' : ''}`}>
            <label>atau Impor Peserta Kegiatan</label>
            <input
              type="file"
              ref={fileInput}
              disabled={isLoading || !canSave}
              className="form-control"
              onChange={addPeserta}
              accept=".xls,.xlsx"
            />
            <span className="help-block">{ fieldErrorMessage('PESERTA_DETAIL', errors) }</span>
          </div>

          <button
            className="btn btn-primary"
            disabled={isLoading}
            style={{ marginTop: '5px' }}
            onClick={() => downloadTemplate()}
          >
            Download Template
          </button>

          <div className="callout callout-info" style={{ marginTop: '10px' }}>
            <h4>Catatan dalam Menggunakan Template</h4>
            <p>Mohon untuk tidak mengubah judul kolom pada <i>template</i> yang diberikan karena akan mengakibatkan sistem tidak dapat membaca data pada berkas excel yang Anda unggah. Anda dapat menghapus / menambahkan nama pada <i>template</i> yang diberikan.</p>
          </div>
        </div>
        <div className="box-body table-responsive no-padding" style={{ maxHeight: '200px' }}>
          <table className="table table-hover">
            <thead>
              <tr>
                <th className="sticky">Nama Peserta</th>
                <th className="sticky">ID</th>
                <th className="sticky">Keterangan</th>
                <th className="sticky"></th>
              </tr>
            </thead>
            <tbody>
              {renderPeserta()}
            </tbody>
          </table>
        </div>
        <div className={`overlay ${isLoading ? 'active' : null}`}>
          <i className="fa fa-refresh fa-spin"></i>
        </div>
      </div>
    </React.Fragment>
  );
};